/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { Text, Button, SegmentedControl } from '@/shared/ui';
import { Input } from '@/shared/ui/components/shadcn/input';
import { useAuth } from '@/context/authentication';
import { useLiveClock, getGreetingKey, goToNewAgency } from './use-workspace-helpers';
import { WorkspaceCard } from './WorkspaceCard';
import { PendingAgencies } from './PendingAgencies';
import { WorkspaceDeleteDialog } from './WorkspaceDeleteDialog';
import { AgenciesService } from '@services/requests';
import { formatLocalizedDate } from '@/shared/utils/date';
import {
  partitionWorkspaceAgencies,
  workspaceCardDestination,
} from './workspace-classification';
import type { AgencyDashboardItem } from './ManageAgenciesCards';

const SEARCH_THRESHOLD = 5;

type TabId = 'active' | 'pending';

type WorkspaceSelectorProps = {
  agencies: AgencyDashboardItem[];
  /** Re-fetches the agency list; called after a draft is abandoned. */
  /** Refetches the list after a draft is dropped from the attention rows. */
  onAgencyRemoved?: () => void;
};

/**
 * Chooses responsive Tailwind grid-column classes for the workspace card grid
 * based on how many cards are shown (centering when there are one or two).
 *
 * @param count - Number of agency cards to lay out.
 * @returns The grid class string for the given card count.
 */
function getGridClasses(count: number): string {
  if (count <= 1) return 'grid-cols-1 max-w-md mx-auto';
  if (count === 2) return 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto';
  return 'grid-cols-1 md:grid-cols-3';
}

/**
 * Picks the tab actually shown, overriding the selection when one side is empty.
 *
 * @param selected - The tab the user last chose.
 * @param activeCount - Number of running offices.
 * @param pendingCount - Number of offices waiting on their owner.
 * @returns The tab to render.
 */
function resolveTab(selected: TabId, activeCount: number, pendingCount: number): TabId {
  if (pendingCount === 0) return 'active';
  if (activeCount === 0) return 'pending';
  return selected;
}

/**
 * Workspace landing screen listing the user's agencies. Renders a greeting with
 * a live clock, active/pending tabs, optional search, and a grid of workspace
 * cards plus a "new office" tile, routing active agencies into their workspace
 * and pending ones into the subscription flow.
 *
 * @param props.agencies - The agencies to display and select from.
 * @returns The workspace selector screen.
 */
export const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({ agencies, onAgencyRemoved }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('@pages/agencies');
  const auth = useAuth();
  const clock = useLiveClock();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'active' | 'pending'>('active');
  const [pendingDelete, setPendingDelete] = useState<AgencyDashboardItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const formattedDate = formatLocalizedDate(new Date(), undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const profile = auth.user?.profile as Record<string, unknown> | undefined;
  const userEmail = (profile?.email as string) ?? null;
  const firstName = (profile?.given_name as string)
    || (profile?.full_name as string)?.split(' ')[0]
    || undefined;
  const hasName = Boolean(firstName);

  const { active: activeAgencies, pending: pendingAgencies } = partitionWorkspaceAgencies(
    agencies,
    userEmail,
  );

  /* The search belongs to the grid of running offices: the attention list is
     short by nature and hiding part of it behind a query is the tab problem
     wearing a different hat. */
  const showSearch = activeAgencies.length > SEARCH_THRESHOLD;
  const filtered = showSearch && search
    ? activeAgencies.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()))
    : activeAgencies;

  /* Nothing running means there is nothing on the other side to show. And
     once the last pending office is dropped, the tabs disappear with it, so
     a 'pending' selection would leave an empty screen with no way back. */
  const currentTab = resolveTab(tab, activeAgencies.length, pendingAgencies.length);

  const counterKey = filtered.length === 1 ? 'workspace.counter_one' : 'workspace.counter_other';

  function handleCardClick(agency: AgencyDashboardItem) {
    navigate(workspaceCardDestination(agency));
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await AgenciesService.abandonAgency(pendingDelete.id);
      setPendingDelete(null);
      onAgencyRemoved?.();
    } catch {
      setDeleteError(t('workspace.deleteDraft.error'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="bg-(--surface-primary) min-h-full flex flex-col">
      <div className="flex flex-col px-(--space-4) py-(--space-4) md:p-(--space-10)">
        <div className="w-full max-w-5xl mx-auto">
          {/* Greeting */}
          <div className="mb-(--space-6) md:mb-(--space-10)">
            <div className="flex items-center justify-between">
              <Text as="h1" className="text-lg md:text-2xl font-bold text-(--text-primary) m-0 truncate">
                {t(getGreetingKey(hasName), { name: firstName })}
              </Text>
              <Text as="span" className="hidden md:flex items-center gap-(--space-3) text-base text-(--text-tertiary)">
                <Text as="span" className="capitalize" data-testid="current-date">{formattedDate}</Text>
                <Text as="span" className="flex items-center gap-(--space-1)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="animate-[spin_60s_linear_infinite]">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="12" x2="12" y2="7" />
                    <line x1="12" y1="12" x2="15.5" y2="14" className="origin-[12px_12px] animate-[spin_3s_linear_infinite]" />
                  </svg>
                  <Text as="span" data-testid="live-clock">{clock}</Text>
                </Text>
              </Text>
            </div>
            <Text className="text-sm md:text-base text-(--text-tertiary) m-0 mt-(--space-1)">
              {t('workspace.greetingSubtitle')}
            </Text>
          </div>

          {/* Two groups, one at a time: an office that is running and one
              that is waiting on a plan need different things, and the counts
              stay on the control so the waiting ones are visible from the
              other side. */}
          {pendingAgencies.length > 0 && (
            <div className="mb-(--space-5)">
              <SegmentedControl
                value={currentTab}
                onChange={(value) => { setTab(value as 'active' | 'pending'); setSearch(''); }}
                variant="pill"
                size="md"
                testId="workspace-tabs"
                items={[
                  {
                    value: 'active',
                    label: t('workspace.tabActive', { count: activeAgencies.length }),
                    testId: 'tab-active',
                  },
                  {
                    value: 'pending',
                    label: t('workspace.tabPending', { count: pendingAgencies.length }),
                    testId: 'tab-pending',
                  },
                ]}
              />
            </div>
          )}

          {currentTab === 'pending' && (
            <PendingAgencies
              agencies={pendingAgencies}
              onOpen={handleCardClick}
              onDelete={setPendingDelete}
            />
          )}

          {currentTab === 'active' && (
            <>
          <div className={cn('flex items-center mb-(--space-4) md:mb-(--space-5)', filtered.length <= 1 ? 'justify-center' : 'justify-between')}>
            <Text data-testid="workspace-counter" className="text-sm md:text-base text-(--text-tertiary) m-0">
              {t(counterKey, { count: filtered.length })}
            </Text>
            {showSearch && (
              <Input
                type="text"
                data-testid="workspace-search"
                placeholder={t('workspace.searchPlaceholder')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                className="px-(--space-4) py-(--space-2) text-sm md:text-base rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary) placeholder:text-(--text-tertiary) outline-none focus:border-(--seed-base) w-36 md:w-52 transition-colors"
              />
            )}
          </div>

          <div data-testid="workspace-grid" className={cn('grid gap-(--space-3) md:gap-(--space-4)', getGridClasses(filtered.length))}>
            {filtered.map((agency) => (
              <WorkspaceCard
                key={agency.id}
                agency={agency}
                onClick={() => handleCardClick(agency)}
              />
            ))}
            <Button
              variant="none"
              disableSize
              data-testid="ghost-card"
              onClick={() => goToNewAgency(navigate)}
              className="flex items-center justify-center gap-(--space-2) p-(--space-4) md:p-(--space-5) rounded-(--radius-lg) border border-dashed border-(--border-subtle) bg-transparent cursor-pointer hover:border-(--seed-base) hover:text-(--seed-base) transition-colors text-(--text-tertiary)"
            >
              <Text as="span" className="text-xl md:text-2xl">+</Text>
              <Text as="span" className="text-sm md:text-base font-medium">{t('workspace.newOffice')}</Text>
            </Button>
          </div>
            </>
          )}
        </div>
      </div>

      <WorkspaceDeleteDialog
        agencyName={pendingDelete?.name ?? ''}
        open={pendingDelete !== null}
        busy={deleting}
        error={deleteError}
        onCancel={() => { setPendingDelete(null); setDeleteError(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
